import React from 'react';
import { Box } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useLevels, LevelConfig } from '../context/LevelContext';

interface LevelDisplayProps {
  level: number;
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  clickable?: boolean;
}

const BADGE_COLORS: Record<string, string[]> = {
  bronze: ['#CD7F32', '#8B4513'],
  silver: ['#C0C0C0', '#A9A9A9'],
  gold: ['#FFD700', '#DAA520'],
  diamond: ['#7DD3F8', '#1B7FC4'],
};

const LevelDisplay: React.FC<LevelDisplayProps> = ({ level, size = 'medium', clickable = true }) => {
  const { getLevelByIndex } = useLevels();
  const navigate = useNavigate();
  const { userId } = useParams();
  const levelConfig = getLevelByIndex(level);

  const sizeConfig = {
    xsmall: { width: 24, height: 24 },
    small: { width: 28, height: 28 },
    medium: { width: 40, height: 40 },
    large: { width: 60, height: 60 },
  };

  const { width, height } = sizeConfig[size];

  // Fallback to gradient if level not found
  const fallbackStyle = {
    width,
    height,
    borderRadius: '50%',
    background: `linear-gradient(135deg, #9E9E9E 0%, #616161 100%)`,
    boxShadow: 2,
  };

  if (!levelConfig) {
    return <Box sx={fallbackStyle} />;
  }

  // Use level image from public folder
  const imagePath = `/levels/${levelConfig.id}.svg`;

  const getFallbackGradient = (config: LevelConfig): string => {
    const badgeColors = BADGE_COLORS[config.badge] || ['#9E9E9E', '#616161'];
    return `linear-gradient(135deg, ${badgeColors[0]} 0%, ${badgeColors[1]} 100%)`;
  };

  const handleClick = () => {
    if (clickable && userId) {
      navigate(`/user/${userId}/levels`);
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        width,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: clickable ? 'pointer' : 'default',
      }}
    >
      <Box
        component="img"
        src={imagePath}
        alt={levelConfig.name}
        sx={{
          width,
          height,
          objectFit: 'contain',
        }}
        onError={(e) => {
          // Fallback to gradient if image fails to load
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          if (target.parentElement) {
            target.parentElement.style.background = getFallbackGradient(levelConfig);
            target.parentElement.style.borderRadius = '50%';
          }
        }}
      />
    </Box>
  );
};

export default LevelDisplay;